import React, { useRef, useEffect, useState, memo, useCallback, useMemo } from 'react';
import axios from 'axios';
import Hls from 'hls.js';
import styles from './BeatItem.module.css';
import playIcon from '../icons/play.png';
import pauseIcon from '../icons/pause.png';
import profileOfflineIcon from '../icons/profileoffline.png';
import CheckoutModal from './CheckoutModal';
import { CMD_EVENT, REG_EVENT, UNREG_EVENT, STATE_EVENT } from './expandable-pro-player/player-bus';

const BeatItem = ({ id, coverImage, title, genre, bpm, keySignature, moodTags, priceWav, priceKit, sendEvent, isPlaying, onPlay, onTagClick, selectedTags, highlighted, isAuthenticated, artistSticker }) => {
  const audioRef = useRef(null);
  const hlsRef = useRef(null);
  const currentTrackIdRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const itemRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSpinner, setShowSpinner] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [checkoutTx, setCheckoutTx] = useState(null);
  const pausedByCheckout = useRef(false);

  const absoluteCover = useMemo(
    () => new URL(coverImage, window.location.origin).href,
    [coverImage]
  );
 
  const setMediaMetadata = useCallback(() => {
  if (!('mediaSession' in navigator)) return;
  navigator.mediaSession.metadata = new MediaMetadata({
    title,
    artist: 'Quality Performance Core',
    album : 'qpCore Beats',
    artwork: [
      { src: absoluteCover, sizes: '512x512', type: 'image/png' },
      { src: absoluteCover, sizes: '256x256', type: 'image/png' }
    ]
  });
}, [title, absoluteCover]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
        setMessageType(null);
      }, 2100);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleShare = () => {
  const shareUrl = `${window.location.origin}/share?id=${id}`;
  if (navigator.share) {
    navigator
      .share({
        title: `Check out this beat: ${title}`,
        url: shareUrl,
      })
      .catch(() =>
        setMessage({
          text: 'Unable to share. Please copy the link manually!',
          type: 'error',
        })
      );
  } else {
    navigator.clipboard.writeText(shareUrl);
    setMessage({ text: 'Share link copied to clipboard!', type: 'success' });
  }
};

  useEffect(() => {
    if (isAuthenticated) {
      axios
        .get('/api/favorites/list')
        .then((response) => {
          const liked = response.data.some((beat) => beat.id === id);
          setIsLiked(liked);
        })
	.catch(() => {});
    } else {
      setIsLiked(false);
    }
  }, [id, isAuthenticated]);

  const toggleLike = () => {
    if (!isAuthenticated) {
      setMessage({
        text: (
          <span>
            Tap <img src={profileOfflineIcon} alt="Icon" className={styles.inlineIcon} /> to drop some likes on beats!
          </span>
        ),
        type: 'error',
      });
      return;
    }

    if (isLiked) {
      axios
        .post('/api/favorites/remove', { beatId: id })
        .then(() => {
          setIsLiked(false);
          window.dispatchEvent(new CustomEvent('likedBeatsChanged'));
	  setMessage({
            text: 'Beat removed from your favorites.',
            type: 'success',
          });
        })
        .catch(() =>
	  setMessage({
            text: 'Failed to remove the beat. Try again later!',
            type: 'error',
          })
        );
    } else {
      axios
        .post('/api/favorites/add', { beatId: id })
        .then(() => {
          setIsLiked(true);
          window.dispatchEvent(new CustomEvent('likedBeatsChanged'));
	  setMessage({
            text: 'Beat added to your favorites!',
            type: 'success',
          });
        })
        .catch(() =>
          setMessage({
            text: 'Failed to add the beat. Try again later!',
            type: 'error',
          })
        );
    }
  };

    useEffect(() => {
    const audio = audioRef.current;
    const handleEnded = () => {
      onPlay(null);
      setIsAudioPlaying(false);
    };
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onPlay]);

  useEffect(() => {
    const audio = audioRef.current;
    const updateTime = () => {
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration);
    };
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateTime);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateTime);
    };
  }, []);

useEffect(() => {
  const audio = audioRef.current;

  const syncState = () => {
    const isCurrentlyPlaying = !audio.paused && !audio.ended;
    if (isCurrentlyPlaying !== isPlaying) {
      onPlay(isCurrentlyPlaying ? id : null);
    }
  };

  audio.addEventListener('play', syncState);
  audio.addEventListener('pause', syncState);
  audio.addEventListener('ended', syncState);

  return () => {
    audio.removeEventListener('play', syncState);
    audio.removeEventListener('pause', syncState);
    audio.removeEventListener('ended', syncState);
  };
}, [isPlaying, onPlay, id]);

  const getPlaylistUrl = useCallback(async (trackId) => {
    // hlsId: fetch(`/api/hls/url/${hlsId}`)
    const r = await fetch(`/api/hls/url/by-beat/${trackId}`);
    if (!r.ok) throw new Error('Failed to get playlist URL');
    const { playlist } = await r.json();
    return playlist;
  }, []);

  const getBufferedAhead = (media) => {
    if (!media) return 0;
    const { buffered, currentTime } = media;
    for (let i = 0; i < buffered.length; i++) {
      const start = buffered.start(i);
      const end = buffered.end(i);
      if (currentTime >= start && currentTime <= end) {
        return Math.max(0, end - currentTime);
      }
    }
    return 0;
  };

  const bufferMonitorRef = useRef(null);
  const seekHandlerRef = useRef(null);
  const lastStartRef = useRef(0);
  const lastStopRef  = useRef(0);
  const isSeekingRef = useRef(false);
  const seekedHandlerRef = useRef(null); 

  const destroyHls = useCallback(() => {
    const a = audioRef.current;
    if (bufferMonitorRef.current && a) {
      a.removeEventListener('timeupdate', bufferMonitorRef.current);
      bufferMonitorRef.current = null;
    }

    if (seekHandlerRef.current && a) {
    a.removeEventListener('seeking', seekHandlerRef.current);
    seekHandlerRef.current = null;
    }

    if (seekedHandlerRef.current && a) {
    a.removeEventListener('seeked', seekedHandlerRef.current);
    seekedHandlerRef.current = null;
    }

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
      lastStartRef.current = 0;
      lastStopRef.current  = 0;
      isSeekingRef.current = false;
  }, []);

  const updatePositionState = useCallback(() => {
    if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return;
    const a = audioRef.current;
    if (!a) return;
    const dur = isFinite(a.duration) ? a.duration : 0;
    navigator.mediaSession.setPositionState({
      duration: dur,
      position: a.currentTime || 0,
      playbackRate: a.playbackRate || 1
    });
  }, []);

  const setupHlsAndPlay = useCallback(async (trackId) => {
  const audioEl = audioRef.current;
  if (!audioEl) return;

  destroyHls();

  const playlist = await getPlaylistUrl(trackId);
  currentTrackIdRef.current = trackId;

  if (Hls.isSupported()) {
    const hls = new Hls({
      autoStartLoad: true,
      maxBufferLength: 3,
      maxMaxBufferLength: 6,
      backBufferLength: 0,
      lowLatencyMode: false
    });
    hlsRef.current = hls;
    hls.loadSource(playlist);
    hls.attachMedia(audioEl);

    let stopMargin   = 6;
    let resumeMargin = 2;

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      const td = hls.levels?.[hls.currentLevel]?.details?.targetduration;
      if (td) {
        stopMargin   = Math.max(3, td * 2.5);
        resumeMargin = Math.max(1, td * 1.0);
      }
      audioEl.play().then(updatePositionState).catch(() => {});
    });

    hls.on(Hls.Events.FRAG_BUFFERED, () => {
      if (isSeekingRef.current) return; 
      const ahead = getBufferedAhead(audioEl);
      const now   = performance.now();
      if (ahead > stopMargin && now - lastStopRef.current > 500) {
        hls.stopLoad();
        lastStopRef.current = now;
      }
    });

    if (bufferMonitorRef.current) {
      audioEl.removeEventListener('timeupdate', bufferMonitorRef.current);
      bufferMonitorRef.current = null;
    }
    bufferMonitorRef.current = () => {
      if (isSeekingRef.current) return;
      const ahead = getBufferedAhead(audioEl);
      const now   = performance.now();
      if (ahead < resumeMargin && now - lastStartRef.current > 500) {
        hlsRef.current?.startLoad();
        lastStartRef.current = now;
      }
    };
    audioEl.addEventListener('timeupdate', bufferMonitorRef.current);

    if (seekHandlerRef.current) {
      audioEl.removeEventListener('seeking', seekHandlerRef.current);
      seekHandlerRef.current = null;
    }
    seekHandlerRef.current = () => {
      isSeekingRef.current = true;
      const pos = (audioEl.currentTime || 0) + 0.05;
      lastStartRef.current = performance.now();
      hlsRef.current?.startLoad(pos);
    };
    audioEl.addEventListener('seeking', seekHandlerRef.current);

    if (seekedHandlerRef.current) {
      audioEl.removeEventListener('seeked', seekedHandlerRef.current);
      seekedHandlerRef.current = null;
    }
    seekedHandlerRef.current = () => { 
      isSeekingRef.current = false;
      lastStartRef.current = performance.now();
    };
    audioEl.addEventListener('seeked', seekedHandlerRef.current);

    hls.on(Hls.Events.ERROR, async (_evt, data) => {
      if (data.fatal && data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        try {
          const fresh = await getPlaylistUrl(currentTrackIdRef.current);
          hls.loadSource(fresh);
          hls.startLoad();
        } catch (e) {
          console.error('HLS refresh failed', e);
        }
      }
    });

  } else if (audioEl.canPlayType('application/vnd.apple.mpegurl')) {
    audioEl.src = playlist;
    await audioEl.play().then(updatePositionState).catch(() => {});
  } else {
    console.error('HLS is not supported in this browser.');
  }
}, [destroyHls, getPlaylistUrl, updatePositionState]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return;
    const a = audioRef.current;
    if (!a) return;

    const handler = () => updatePositionState();

    a.addEventListener('timeupdate', handler);
    a.addEventListener('durationchange', handler);
    a.addEventListener('seeked', handler);
    a.addEventListener('ratechange', handler);

    updatePositionState();

    return () => {
      a.removeEventListener('timeupdate', handler);
      a.removeEventListener('durationchange', handler);
      a.removeEventListener('seeked', handler);
      a.removeEventListener('ratechange', handler);
    };
  }, [updatePositionState]);

  useEffect(() => {
  const audio = audioRef.current;
  if (!audio) return;

  const start = async () => {
    if (!audio.paused) return;

    if (!audio.src && !hlsRef.current) {
      try {
        await setupHlsAndPlay(id);
        setMediaMetadata();
        setIsAudioPlaying(true);
        if ('mediaSession' in navigator) {
          navigator.mediaSession.setActionHandler('play',  () => onPlay(id));
          navigator.mediaSession.setActionHandler('pause', () => onPlay(null));
        }

        updatePositionState();
      } catch (err) {
        console.error(err);
        setIsAudioPlaying(false);
      }
    } else {
      audio.play()
        .then(() => {
            setMediaMetadata();
            setIsAudioPlaying(true);
            updatePositionState();
          })
        .catch(() => {});
    }
  };

  const stop = () => {
    if (audio.paused) return;
    audio.pause();
    setIsAudioPlaying(false);

    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', null);
      navigator.mediaSession.setActionHandler('pause', null);
    }
  };

  if (isPlaying) start(); else stop();
}, [isPlaying, isAudioPlaying, onPlay, id, setupHlsAndPlay, updatePositionState, setMediaMetadata]);

  useEffect(() => {
  if (isPlaying) setMediaMetadata();
}, [isPlaying, setMediaMetadata]);

    useEffect(() => destroyHls, [destroyHls]);

    // === BUS: helpers ===
	const emit = (type, detail) => window.dispatchEvent(new CustomEvent(type, { detail }));

	const emitRegister = useCallback(() => {
	  emit(REG_EVENT, {
	    id,
	    title,
	    artist: 'Quality Performance Core',      // либо прокинь artist пропом, если есть
	    cover: coverImage,
	    format: 'hls',                           // у тебя HLS, можно 'mp3' для mp3
	    duration: isFinite(duration) ? duration : undefined,
	  });
	}, [id, title, coverImage, duration]);

	const emitUnregister = useCallback(() => {
	  emit(UNREG_EVENT, { id });
	}, [id]);

	const emitState = useCallback((playingVal) => {
	  const a = audioRef.current;
	  const dur = a && isFinite(a.duration) ? a.duration : (isFinite(duration) ? duration : 0);
	  emit(STATE_EVENT, {
	    id,
	    playing: !!playingVal,
	    currentTime: a ? a.currentTime || 0 : 0,
	    duration: dur || 0,
	  });
	}, [id, duration]);

	// при маунте/анмаунте — сообщаем о наличии карточки
	useEffect(() => {
	  emitRegister();
	  return () => emitUnregister();
	}, [emitRegister, emitUnregister]);

	// слать состояние при изменениях аудио
	useEffect(() => {
	  const a = audioRef.current;
	  if (!a) return;

	  const onPlay = () => emitState(true);
	  const onPause = () => emitState(false);
	  const onEnded = () => emitState(false);
	  const onTick = () => emitState(!a.paused && !a.ended);

	  a.addEventListener('play', onPlay);
	  a.addEventListener('pause', onPause);
	  a.addEventListener('ended', onEnded);
	  a.addEventListener('timeupdate', onTick);
	  a.addEventListener('loadedmetadata', onTick);
	  a.addEventListener('durationchange', onTick);

	  // первый пуш состояния (если нужно)
	  emitState(!a.paused && !a.ended);

	  return () => {
	    a.removeEventListener('play', onPlay);
	    a.removeEventListener('pause', onPause);
	    a.removeEventListener('ended', onEnded);
	    a.removeEventListener('timeupdate', onTick);
	    a.removeEventListener('loadedmetadata', onTick);
	    a.removeEventListener('durationchange', onTick);
	  };
	}, [emitState]);

	// принимать команды от глобального плеера (только если id совпадает)
	useEffect(() => {
	  const handler = (e) => {
	    const { type, id: targetId, seconds, pct, volume } = e.detail || {};
	    if (targetId !== id) return;

	    const a = audioRef.current;
	    if (!a) return;

	    switch (type) {
	      case 'play':
		// Твоя логика запуска уже завязана на prop isPlaying → onPlay(id)
		onPlay(id);
		break;
	      case 'pause':
		onPlay(null);
		break;
	      case 'toggle':
		onPlay(isPlaying ? null : id);
		break;
	      case 'seek':
		if (typeof seconds === 'number') {
		  a.currentTime = Math.max(0, seconds);
		  emitState(!a.paused && !a.ended);
		}
		break;
	      case 'seekPct': {
		const dur = isFinite(a.duration) ? a.duration : 0;
		if (dur && typeof pct === 'number') {
		  a.currentTime = Math.max(0, Math.min(1, pct / 100)) * dur;
		  emitState(!a.paused && !a.ended);
		}
		break;
	      }
	      case 'volume':
		if (typeof volume === 'number') {
		  a.volume = Math.max(0, Math.min(1, volume));
		  // если хочешь — сохрани локально, но это не обязательно
		}
		break;
	      default:
		break;
	    }
	  };

	  window.addEventListener(CMD_EVENT, handler);
	  return () => window.removeEventListener(CMD_EVENT, handler);
	}, [id, isPlaying, onPlay, emitState]);

    const handleBuy = async (amount, beatId, productType) => {

     if (audioRef.current && !audioRef.current.paused) {
       audioRef.current.pause();
       onPlay(null);
       pausedByCheckout.current = true;
     } else {
      pausedByCheckout.current = false;
     }

     if (!isAuthenticated) {
      setMessage({
        text: (
          <span>
            Tap to <img src={profileOfflineIcon} alt="Icon" className={styles.inlineIcon} /> before you grab that!
          </span>
        ),
        type: 'error',
      });
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId) {
      setMessage({
        text: (
          <span>
            Tap to <img src={profileOfflineIcon} alt="Icon" className={styles.inlineIcon} /> before you grab that!
          </span>
        ),
        type: 'error',
      });
      return;
    }

    setIsPreparing(true);

    try {
      await sendEvent(
      `Purchase ${productType.toUpperCase()} for "${title}" (Beat ID: ${beatId})`,
      `Purchase ${productType.toUpperCase()} for "${title}" (Beat ID: ${beatId})`
    );
    } catch (error) {
    }

    try {
      const response = await axios.post('/api/payments/create-invoice', {
        userId,
        amount,
	beatId,
        productType,
      });

    const { transactionId, invoiceUrl, orderId, totals } = response.data;
      setPriceBreakdown(totals);
    if (transactionId) {
      setIsPreparing(false);
      setCheckoutTx({ tx: transactionId, orderId });

    } else if (invoiceUrl) {
      setIsPreparing(false);
      window.location.href = invoiceUrl;
    } else {
      setIsPreparing(false);
      setMessage({
        text: 'There was an issue creating your bill. Try again later!',
        type: 'error',
      });
    }
  } catch (err) {
    setIsPreparing(false);
    setMessage({
      text: 'There was an issue creating your bill. Try again later!',
      type: 'error',
    });
  }
};

  const handleDesktopWaveformClick = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const ratio = clickX / rect.width;
    audioRef.current.currentTime = ratio * duration;
  };

  const handleMobileCoverClick = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const x = e.clientX - centerX;
    const y = e.clientY - centerY;
    const angle = Math.atan2(x, -y); 
    let deg = angle * (180 / Math.PI);
    if (deg < 0) deg += 360;
    const ratio = deg / 360;
    audioRef.current.currentTime = ratio * duration;
  };

  const loadAndPlayAudio = async () => {
    if (!hlsRef.current && !audioRef.current.src) {
      await setupHlsAndPlay(id);
    }
    onPlay(id);
  };

  return (
    <div
     id={`beat-${id}`}
     ref={itemRef} 
     className={`${styles.beatItem} ${highlighted ? styles.highlighted : ''}`}
    >
        {message && (
        <div className={styles.overlay}>
          <div className={`${styles.overlayMessage} ${styles[messageType]}`}>
            {message.text}
          </div>
        </div>
      )}
      <button onClick={loadAndPlayAudio} className={styles.playButton}>
        <img src={isPlaying ? pauseIcon : playIcon} alt="Play/Pause" className={styles.playButtonImage}/>
      </button>

      {isMobile ? (
        <div
          className={styles.coverContainer}
          style={{ '--progress': `${(currentTime / duration) * 360}deg` }}
          onClick={handleMobileCoverClick}
        >
          <img src={coverImage} alt={`${title} cover`} className={styles.beatItemCoverMobile} loading="lazy" />
        </div>
      ) : (
        <div className={styles.beatCover}>
          <img src={coverImage} alt={`${title} cover`} className={styles.beatItemCover} loading="lazy" />
	  {artistSticker && (
          <img
            src={artistSticker}
            alt="Artist Sticker"
            className={`${styles.artistSticker} ${styles.artistStickerDesktop}`}
            onClick={() => onTagClick(artistSticker)}
          />
        )}
        </div>
      )}

      <div className={styles.beatInfo}>
        <h3 className={styles.beatTitle}>{title}</h3>
        <p className={styles.beatGenre}>{genre}</p>
        <div className={styles.beatDetails}>
          <span className={styles.beatBpm}>{bpm} BPM</span>
          <span className={styles.beatKey}>Key: {keySignature}</span>
        </div>
        <div className={styles.beatTags}>
          {moodTags.map((tag, index) => (
	   <button
              key={index}
	      className={`${styles.beatTag} ${selectedTags.includes(tag) ? styles.activeBeatTag : ''}`} 
              onClick={() => onTagClick(tag)} 
            >
              #{tag}
            </button>	  
          ))}
        </div>
      </div>

      {!isMobile && (
        <div
          className={styles.waveformContainer}
          onClick={handleDesktopWaveformClick}
        >
          <div
            className={styles.waveformProgress}
            style={{ width: `${(currentTime / duration) * 100}%` }}
          />
        </div>
      )}

      <div className={styles.beatActions}>
	  <button className={styles.shareButton} onClick={handleShare}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="#555555"
              viewBox="0 0 24 24"
              width="24"
              height="24"
            >
              <path d="M18 16.08c-.76 0-1.44.3-1.96.77l-7.1-4.2a3.11 3.11 0 0 0 0-1.29l7.1-4.2A2.996 2.996 0 1 0 15 5c0 .11.01.21.02.32l-7.1 4.2a3 3 0 1 0 0 4.96l7.1 4.2c-.01.11-.02.21-.02.32a3 3 0 1 0 3-3zM18 4a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm-12 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm12 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
            </svg>
          </button>
	  <button className={styles.beatFavorite} onClick={toggleLike}>
          {isLiked ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="#ffffff" viewBox="0 0 24 24" width="24" height="24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="#555555" viewBox="0 0 24 24" width="24" height="24">
              <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
            </svg>
          )}
        </button>
	{isMobile && artistSticker && (
      <img
        src={artistSticker}
        alt="Artist Sticker"
        className={`${styles.artistSticker} ${styles.artistStickerMobile}`}
        onClick={() => onTagClick(artistSticker)}
      />
    )}
        <div className={styles.beatPrice}>
	  <button className={styles.beatBuyWav} onClick={() => handleBuy(priceWav, id, 'wav')}>
            Buy WAV - ${priceWav.toFixed(2)}
          </button>
          <button className={styles.beatBuyKit} onClick={() => handleBuy(priceKit, id, 'kit')}>
            Buy KIT - ${priceKit.toFixed(2)}
          </button>
        </div>
      </div>
     <audio ref={audioRef} preload="metadata" />

     {checkoutTx && (
	  <CheckoutModal
	    transactionId={checkoutTx.tx}
	    priceBreakdown={priceBreakdown}
	    onClose={() => {
	      setCheckoutTx(null);
	      setIsPreparing(false);
	      setMessage({
		text: "Checkout was closed before payment.",
		type: "error",
	      });
            
	      if (pausedByCheckout.current) {
                pausedByCheckout.current = false;
                audioRef.current.play().catch(() => {});
                onPlay(id);
              }
	    }}
	    onComplete={() => {
	      setShowSpinner(true);
	      setCheckoutTx(null);
	      setTimeout(() => {
		window.location.href = `/?orderId=${checkoutTx.orderId}`;
	      }, 4500);
	    }}
	  />
	)}

     {isPreparing && (
	  <div className={styles.buyOverlay}>
	    <div className={styles.buyPreparing}>PREPARING…</div>
	  </div>
	)}	  

     {showSpinner && (
        <div className={styles.spinnerOverlay}>
          <div className={styles.spinnerLoader}></div>
        </div>
      )}
    </div>
  );
};

export default memo(BeatItem);
